import { keyframes } from '@emotion/react';
import { Box } from '@mui/joy';
import { FocusEvent, memo } from 'react';

import { BellNotificationSurface } from 'notifications/enums';
// By file: the hooks barrel loads notifications/states and, through it, the core barrels.
import { useToastAutoClose } from 'notifications/hooks/useToastAutoClose';
import { IBellNotification, IBellNotificationCta } from 'notifications/types';
import { getNotificationContent } from 'notifications/utils/notification';
import { getNotificationToastAutoCloseMs, isFocusLeaving } from 'notifications/utils/toast';

import { NotificationAlert } from '../NotificationAlert';
import { NotificationContent } from '../NotificationContent';

interface IProps {
  notification: IBellNotification;
  onClick: (notification: IBellNotification) => void;
  onAction: (notification: IBellNotification, cta: IBellNotificationCta) => void;
  onHide: (id: number) => void;
  // The stack holds every countdown while the pointer or the focus is in it.
  isHeld: boolean;
}

const toastEnter = keyframes({
  from: { opacity: 0, transform: 'translateX(24px)' },
  to: { opacity: 1, transform: 'translateX(0)' },
});

// One arrival as an Alert card. Close only hides it: the notification stays unread in the drawer.
export const NotificationToast = memo(function NotificationToast({
  notification,
  onClick,
  onAction,
  onHide,
  isHeld,
}: IProps) {
  const { severity } = getNotificationContent(notification);
  const autoCloseMs = isHeld ? null : getNotificationToastAutoCloseMs();

  const { pause, resume } = useToastAutoClose(autoCloseMs, () => onHide(notification.id));

  const onCardClick = () => onClick(notification);

  const onClose = () => onHide(notification.id);

  const handleOnBlur = (event: FocusEvent<HTMLElement>) => {
    if (isFocusLeaving(event)) {
      resume();
    }
  };

  return (
    <Box
      data-testid="bellNotificationCard"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={handleOnBlur}
      sx={{
        flexShrink: 0,
        animation: `${toastEnter} 200ms ease-out`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <NotificationAlert severity={severity} onClick={onCardClick} onClose={onClose}>
        <NotificationContent
          notification={notification}
          surface={BellNotificationSurface.TOAST}
          onAction={onAction}
        />
      </NotificationAlert>
    </Box>
  );
});
